import type { Ref } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { EmptyState } from '../components/ui/EmptyState'
import { Skeleton } from '../components/ui/Skeleton'
import { categoryParentGroupName } from '../verticals/catalog/catalogTaxonomy'
import { CategoryTileImage } from '../verticals/catalog/components/CategoryTileImage'
import type { CatalogCategory } from '../verticals/catalog/types'
import { defaultCatalogSearchService } from '../verticals/catalog/useCatalogSearch'
import type { CategoryListingService } from '../verticals/catalog/useCategoryTaxonomy'

export interface HomeCategoryCarouselProps {
  /** Injectable for tests — same seam `CatalogBrowsePage` exposes; defaults
   * to the real BFF-backed `GET /catalog/categories`. */
  readonly service?: CategoryListingService
  /**
   * The mock (`Fictiv Platform.dc.html` ~L461-481) puts the Prev/Next arrows
   * in the section's heading row, beside "View full catalog →" — but that
   * row is rendered by the parent (`HomePage.tsx`, above this component,
   * since it's shared with the "View full catalog" link). Rather than have
   * this component render its own nav row in the wrong place, it forwards
   * the scroll container's DOM ref (the same plain `ref?: Ref<HTMLElement>`
   * pattern `Button`/`RfqPartsCard` already use for DOM-ref props, not a
   * custom imperative handle) so `HomePage` can drive
   * `scrollBy` from its own Prev/Next buttons. `null` while loading/error/
   * empty, since there's nothing to scroll yet.
   */
  readonly ref?: Ref<HTMLDivElement>
}

type CarouselState =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly message: string }
  | { readonly status: 'ready'; readonly categories: readonly CatalogCategory[] }

const TINT_CLASSES = [
  'catalog-group-tint-0',
  'catalog-group-tint-1',
  'catalog-group-tint-2',
  'catalog-group-tint-3',
] as const

/** "146 series" alone, or "146 series · Linear Motion" when the category has
 * a parent taxonomy group (`catalogTaxonomy.ts`) — omits the parent clause
 * entirely for a category not listed in any group, rather than showing the
 * literal "Other". */
function seriesCountLabel(count: number, parentGroup: string | null): string {
  return parentGroup === null ? `${count} series` : `${count} series · ${parentGroup}`
}

/**
 * The Home "Browse the catalog" off-the-shelf category carousel (UID-250,
 * design refresh README §7 screen 1) — replaces the `<EmptyState>` stub that
 * previously stood in for this section (`HomePage.tsx`, "Coming in WS-2").
 *
 * Reuses the exact same `GET /catalog/categories` data (via
 * `CategoryListingService`, the seam `CatalogBrowsePage.tsx`'s "Shop by
 * category" grid already consumes for UID-253) and the exact same
 * click-through destination (`/catalog/series/:category`, `CatalogSeriesPage`
 * from UID-286) — this is a third consumer of both, not a new contract.
 *
 * Each card renders the category's real, BFF-derived `imageUrl` (a
 * representative image from one of the category's own series — see
 * `category-catalog-enrichment.ts`), right-aligned and larger than the
 * fallback glyph, via `CategoryTileImage`, falling back to the generic
 * tinted `IconCategoryGrid` glyph when no real image is available. The
 * subtitle below the name is `seriesCount` plus the category's parent
 * taxonomy group (`catalogTaxonomy.ts`'s `categoryParentGroupName`, the same
 * static grouping `ShopByCategory`'s group headers use) — e.g. "146 series ·
 * Linear Motion" — never fabricated (ADR-0090/0091 "omit, don't stub"): the
 * count is omitted when null, and the parent clause is omitted for a
 * category not listed in any group. Tint cycles the same 4-color palette by
 * index so adjacent cards read distinctly.
 */
export function HomeCategoryCarousel({
  service = defaultCatalogSearchService,
  ref,
}: HomeCategoryCarouselProps) {
  const [state, setState] = useState<CarouselState>({ status: 'loading' })
  const serviceRef = useRef(service)
  serviceRef.current = service
  const navigate = useNavigate()

  const load = useCallback(() => {
    setState({ status: 'loading' })
    let cancelled = false
    serviceRef.current
      .listCategories()
      .then((categories) => {
        if (!cancelled) setState({ status: 'ready', categories })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        const message =
          error instanceof Error ? error.message : 'We couldn’t load the catalog categories.'
        setState({ status: 'error', message })
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => load(), [load])

  function handleCategoryClick(category: string) {
    navigate(`/catalog/series/${encodeURIComponent(category)}`)
  }

  if (state.status === 'loading') {
    return (
      <output className="home-category-carousel-scroll" aria-busy="true">
        <span className="sr-only">Loading categories…</span>
        {Array.from({ length: 8 }, (_, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: a fixed-count skeleton row has no stable identity to key by.
          <Skeleton key={index} height={82} variant="block" />
        ))}
      </output>
    )
  }

  if (state.status === 'error') {
    return (
      <EmptyState
        title="We couldn’t load the catalog categories"
        detail={state.message}
        action={<Button onClick={load}>Try again</Button>}
      />
    )
  }

  if (state.categories.length === 0) {
    return (
      <EmptyState
        title="No categories to browse yet"
        detail="The catalog category list came back empty."
      />
    )
  }

  return (
    <div className="home-category-carousel-scroll" ref={ref}>
      {state.categories.map((category, index) => (
        <button
          key={category.name}
          type="button"
          className="home-category-carousel-card"
          onClick={() => handleCategoryClick(category.name)}
        >
          <span className="home-category-carousel-card-text">
            <span className="home-category-carousel-card-name">{category.name}</span>
            {category.seriesCount != null && (
              <span className="home-category-carousel-card-count">
                {seriesCountLabel(category.seriesCount, categoryParentGroupName(category.name))}
              </span>
            )}
          </span>
          <CategoryTileImage
            imageUrl={category.imageUrl}
            tintClass={TINT_CLASSES[index % TINT_CLASSES.length] ?? TINT_CLASSES[0]}
            className="home-category-carousel-card-icon"
            iconSize={20}
          />
        </button>
      ))}
    </div>
  )
}
